/**
 * Executive-facing copy helpers for approval artifacts (One-Pager, CFO ROI, Board Rationale).
 * DMD-1206: strip leaked Markdown, expand BV/EOI, and clean duplicated units in outcomes.
 */

/** Expand scoring abbreviations for executive audiences. */
export function expandScoringAbbreviations(text: string): string {
  if (!text) return text;
  // Protect already-expanded forms so we don't nest replacements.
  return text
    .replace(/Business Value \(BV\)/g, '\uE000')
    .replace(/Ease of Implementation \(EOI\)/g, '\uE001')
    .replace(/\bBV\b/g, 'Business Value (BV)')
    .replace(/\bEOI\b/g, 'Ease of Implementation (EOI)')
    .replace(/\uE000/g, 'Business Value (BV)')
    .replace(/\uE001/g, 'Ease of Implementation (EOI)');
}

/**
 * Fix duplicated units from API composition bugs, e.g.:
 * - "target 70% %" → "target 70%"
 * - "target 4 hours hours" → "target 4 hours"
 * - "target 2 weeks weeks" → "target 2 weeks"
 */
export function normalizeRepeatedUnits(text: string): string {
  return String(text ?? '')
    .replace(/(\d+(?:\.\d+)?)\s*%\s*%+/g, '$1%')
    .replace(
      /\b(\d+(?:\.\d+)?)\s+(hours?|days?|weeks?|months?|years?|mins?|minutes?|secs?|seconds?)\s+\2\b/gi,
      (_m, n: string, unit: string) => `${n} ${unit}`,
    )
    .replace(/\b(hours?|days?|weeks?|months?|years?)\s+\1\b/gi, '$1');
}

/** Remove common Markdown decorations for surfaces that render plain text only. */
export function stripMarkdownDecorators(text: string): string {
  return String(text ?? '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1$2')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/`([^`]+)`/g, '$1')
    // Leftover bold markers (e.g. unmatched **Execute Summary**)
    .replace(/\*\*/g, '')
    .trim();
}

/** Plain-text path: strip Markdown, clean units, expand abbreviations (titles, tags, badges). */
export function formatPlainExecutiveText(text: string | null | undefined): string {
  if (text == null) return '';
  return expandScoringAbbreviations(
    normalizeRepeatedUnits(stripMarkdownDecorators(String(text))),
  );
}

/** Markdown path: clean units + expand abbreviations; leave Markdown for ReactMarkdown. */
export function formatMarkdownExecutiveText(text: string | null | undefined): string {
  if (text == null) return '';
  return expandScoringAbbreviations(normalizeRepeatedUnits(String(text)));
}

/** Expected-outcome lines (plain list items). */
export function formatExpectedOutcomeText(text: string | null | undefined): string {
  return formatPlainExecutiveText(text);
}

/**
 * DMD-1229: Primary Value Driver often pairs total annual benefit with a % × base
 * that does not multiply to the same dollar amount (adoption / model netting omitted).
 * Rewrite inconsistent "driven by X% … on $YM base" copy so the math is explained.
 */
export function normalizePrimaryValueDriverText(text: string | null | undefined): string {
  if (text == null) return '';
  const raw = String(text).trim();
  if (!raw) return '';

  const match = raw.match(
    /^\$([\d,.]+)\s*M\s+annual benefit driven by\s+([\d.]+)%\s+(operational cost reduction|revenue uplift)\s+on\s+\$([\d,.]+)\s*M\s+base(.*)$/i,
  );
  if (!match) return raw;

  const benefitM = Number(match[1].replace(/,/g, ''));
  const pct = Number(match[2]);
  const driverLabel = match[3];
  const baseM = Number(match[4].replace(/,/g, ''));
  const rest = match[5] ?? '';

  if (![benefitM, pct, baseM].every(Number.isFinite) || baseM <= 0 || benefitM < 0) {
    return raw;
  }

  const grossM = (pct / 100) * baseM;
  const denom = Math.max(benefitM, grossM, 0.01);
  if (Math.abs(grossM - benefitM) / denom <= 0.05) {
    return raw;
  }

  const benefitLabel = Number.isInteger(benefitM) ? benefitM.toFixed(0) : benefitM.toFixed(2);
  const baseLabel = Number.isInteger(baseM) ? baseM.toFixed(1) : baseM.toFixed(1);
  const grossLabel = grossM >= 10 ? grossM.toFixed(1) : grossM.toFixed(2);

  return (
    `$${benefitLabel}M annual benefit, primarily from a ${pct.toFixed(1)}% ${driverLabel} ` +
    `on a $${baseLabel}M base (gross $${grossLabel}M before adoption and model adjustments)` +
    rest
  );
}

/** Map API dimension type codes to executive-readable labels. */
export function formatDimensionTypeLabel(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const lower = s.toLowerCase().replace(/[_-]+/g, ' ');
  if (lower === 'bv' || lower === 'value' || lower === 'business value') {
    return 'Business Value';
  }
  if (
    lower === 'eoi' ||
    lower === 'ease' ||
    lower === 'implementation' ||
    lower === 'ease of implementation'
  ) {
    return 'Ease of Implementation';
  }
  return expandScoringAbbreviations(s);
}

const APPROVAL_ARTIFACT_IDS = new Set([
  'executive_one_pager',
  'cfo_roi_summary',
  'board_rationale',
  'assumptions_appendix',
]);

/** Resolve Command Center / playbook resource ids to approval-artifact API ids. */
export function resolveApprovalArtifactId(resourceId: string | null | undefined): string | null {
  if (!resourceId) return null;
  const id = String(resourceId).trim().toLowerCase().replace(/-/g, '_');
  if (APPROVAL_ARTIFACT_IDS.has(id)) return id;
  if (id.includes('executive') && id.includes('one')) return 'executive_one_pager';
  if (id.includes('cfo') || (id.includes('roi') && id.includes('summary'))) return 'cfo_roi_summary';
  if (id.includes('board') && id.includes('rationale')) return 'board_rationale';
  if (id.includes('assumption')) return 'assumptions_appendix';
  return null;
}
